import { useMemo } from "react";
import { useAuthToken } from "@/auth/use-auth-token";
import { captureException } from "@/common/sentry";
import type { TypedDocumentString } from "./artemis-sdk";

export class ForbiddenError extends Error {
	constructor() {
		super("Access denied. Action not allowed.");
		this.name = "ForbiddenError";
	}
}

export class ServerError extends Error {
	constructor() {
		super("Server error. 500");
		this.name = "ServerError";
	}
}

export class NotAuthorizedError extends Error {
	constructor() {
		super("Not Authorized. 401");
		this.name = "NotAuthorizedError";
	}
}

/** GraphQL-level error (non-FORBIDDEN) carrying the server message and extensions.code. */
export class GraphQLRequestError extends Error {
	constructor(
		message: string,
		public readonly code?: string,
	) {
		super(message);
		this.name = "GraphQLRequestError";
	}
}

export function createGraphQLClient(getToken: () => Promise<string | null>) {
	return async function gqlFetch<TResult, TVariables>(
		document: TypedDocumentString<TResult, TVariables>,
		...[variables]: TVariables extends Record<string, never> ? [] : [TVariables]
	): Promise<TResult> {
		const token = await getToken();

		// Operation name (e.g. "GetEnvironment") for Sentry triage. No-op unless
		// Sentry.init ran (prod). Forbidden/NotAuthorized are expected auth states,
		// not reported — matches RootError in __root.tsx.
		const opName =
			document
				.toString()
				.match(/(?:query|mutation|subscription)\s+(\w+)/)?.[1] ?? "anonymous";
		const report = (err: Error, code?: string) =>
			captureException(err, {
				tags: { graphql_op: opName, graphql_code: code },
			});

		// Only network/parse failures map to ServerError — app-level errors
		// thrown below must propagate as-is (a blanket catch here previously
		// turned every ForbiddenError into a generic 500).
		let res: Response;
		let json: any;
		try {
			res = await fetch(import.meta.env.VITE_PLATFORM_API_URL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					// Omitted entirely under the dev auth bypass: the API only falls back to its
					// local identity for requests it sees as unauthenticated.
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({
					query: document.toString(),
					variables,
				}),
			});
			json = res.ok ? await res.json() : undefined;
		} catch {
			const err = new ServerError();
			report(err);
			throw err;
		}

		if (!res.ok) {
			if (res.status === 401) {
				throw new NotAuthorizedError();
			}
			const err =
				res.status === 500
					? new ServerError()
					: new Error(`HTTP ${res.status}`);
			report(err, String(res.status));
			throw err;
		}

		if (json.errors?.length) {
			if (json.errors.some((e: any) => e.extensions?.code === "FORBIDDEN")) {
				throw new ForbiddenError();
			}
			const code = json.errors[0].extensions?.code;
			const err = new GraphQLRequestError(
				json.errors.map((e: any) => e.message).join("; "),
				code,
			);
			report(err, code);
			throw err;
		}

		return json.data;
	};
}

export function useGqlClient() {
	const { getToken } = useAuthToken();

	return useMemo(() => {
		return createGraphQLClient(getToken);
	}, [getToken]);
}
